// ADR 5.9.26i §3 — writing direction.
//
// The same five-member set the native clients use: rust-core's
// speech_plan/scripts.rs maps `["ar", "fa", "prs", "ps", "ckb"]` to the Arabic
// script, and those are exactly the catalogues that must lay out right-to-left.
// Everything else the app ships — including Hebrew-adjacent-looking scripts
// like Persian's neighbours in the picker — is LTR.
//
// The browser needs this on `<html>`, not on a React root: `dir` is inherited,
// and scrollbar placement, text selection and the default `text-align` of every
// element outside the root all read it from the document element.

import type { SupportedLocale } from './resolve'

export const RTL_LOCALES: ReadonlySet<string> = new Set(['ar', 'fa', 'prs', 'ps', 'ckb'])

export type Direction = 'rtl' | 'ltr'

export function directionFor(locale: string): Direction {
  return RTL_LOCALES.has(locale) ? 'rtl' : 'ltr'
}

/**
 * Set `dir` and `lang` on the document element, returning the direction applied.
 *
 * `doc` is a parameter so the shell test can drive it against jsdom's document
 * without a global reach-through.
 */
export function applyDocumentDirection(
  locale: SupportedLocale | string,
  doc: Document = document,
): Direction {
  const direction = directionFor(locale)
  doc.documentElement.setAttribute('dir', direction)
  doc.documentElement.setAttribute('lang', locale)
  return direction
}
